/** The standings a book can be narrowed to — the API's `ComplianceState`, by name. */
export const BOOK_COMPLIANCE = ['Active', 'Pending', 'Blocked'] as const

export type BookCompliance = (typeof BOOK_COMPLIANCE)[number]

/**
 * How the carrier and broker books are asked for — the API's `BookAsk` (A1, 2026-09-28).
 * Asked nothing, a book answers every record by name, up to 500.
 */
export interface BookAsk {
  /** A name fragment or an MC number, however it is spelled. */
  search?: string
  /**
   * How many records to answer, 1 to 501. A limited ask puts the names that start with the
   * search before the ones that merely hold it — the order a person scans a short list in.
   */
  limit?: number
  /** True: only the records we work with; false: only the ones stood down; absent: both. */
  active?: boolean
  /** Only the records whose paperwork stands so (TRKC-97). */
  compliance?: BookCompliance
  /** Only the records whose address is in this state, by its two-letter code (TRKC-97). */
  state?: string
}

/** What a book's own page lists. It asks one more, to know there are more to find by searching. */
export const BOOK_SHOWN = 500

/** The query string an ask travels as; empty when it asks nothing. */
export function bookQuery(ask: BookAsk): string {
  const query = new URLSearchParams()
  const search = ask.search?.trim() ?? ''

  if (search !== '') {
    query.set('search', search)
  }

  if (ask.limit !== undefined) {
    query.set('limit', String(ask.limit))
  }

  if (ask.active !== undefined) {
    query.set('active', String(ask.active))
  }

  if (ask.compliance !== undefined) {
    query.set('compliance', ask.compliance)
  }

  if (ask.state !== undefined) {
    query.set('state', ask.state)
  }

  const text = query.toString()
  return text === '' ? '' : `?${text}`
}
